import {
	useParams,
	useNavigate,
	Navigate,
	Link,
	useLocation
} from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import Page from "src/layouts/Page";
import { useAvatar } from "src/hooks";
import usePerson from "../../hooks/usePerson";
import usePersonProfile from "../../hooks/usePersonProfile";
import useEditPerson from "../../hooks/useEditPerson";
import useAssignGroups from "../../hooks/useAssignGroups";
import useRemoveGroups from "../../hooks/useRemoveGroups";
import { adaptPersonPayload } from "../../adapters/adaptPersonPayload";
import useAdminStores from "src/features/Locations/hooks/useAdminStores";
import useAdminGroups from "src/features/Groups/hooks/useAdminGroups";
import PersonDetailsTab from "../../components/PersonDetailsTab/PersonDetailsTab";
import PersonProfileTab from "../../components/PersonProfileTab/PersonProfileTab";
import PersonGroupsTab from "../../components/PersonGroupsTab/PersonGroupsTab";
import PersonAdminTab from "../../components/PersonAdminTab/PersonAdminTab";

type Tab = "details" | "profile" | "groups" | "admin";

const TABS: { key: Tab; label: string }[] = [
	{ key: "details", label: "Details" },
	{ key: "profile", label: "Profile" },
	{ key: "groups", label: "Groups" },
	{ key: "admin", label: "Admin" }
];

const PersonDetail = () => {
	const { personId } = useParams<{ personId: string }>();
	const navigate = useNavigate();
	const { hash: locationHash } = useLocation();
	const id = personId ?? "";
	const isValidId = /^[\w-]+$/.test(id);

	const hash = locationHash.replace("#", "") as Tab;
	const validTabs: Tab[] = ["details", "profile", "groups", "admin"];
	const activeTab: Tab = validTabs.includes(hash) ? hash : "details";

	const {
		data: personDetail,
		isLoading: isLoadingPerson,
		isError: isPersonError
	} = usePerson(id);

	const { data: profile, isLoading: isLoadingProfile } = usePersonProfile(id, {
		enabled: !!id && !isPersonError
	});

	const { data: avatarSrc, isError: isAvatarError } = useAvatar({
		userId: id
	});

	const { data: stores = [] } = useAdminStores();
	const { data: allGroups = [] } = useAdminGroups();

	const { mutate: editPerson, isLoading: isSaving } = useEditPerson();
	const { mutate: assignGroups, isLoading: isAssigning } = useAssignGroups();
	const { mutate: removeGroups, isLoading: isRemoving } = useRemoveGroups();

	if (!id || !isValidId) {
		return <Navigate to="/users/people" replace />;
	}

	const handleTabChange = (tab: Tab) => {
		navigate(`/users/people/${id}#${tab}`, { replace: true });
	};

	const isLoading = isLoadingPerson;

	if (isLoading) {
		return (
			<Page>
				<div className="flex items-center gap-2 py-12 justify-center">
					<div className="w-5 h-5 border-2 border-border border-t-primary rounded-full animate-spin" />
					<span className="text-sm text-zinc-600">Loading...</span>
				</div>
			</Page>
		);
	}

	if (isPersonError || !personDetail) {
		return (
			<Page>
				<p className="text-sm text-destructive">
					Something went wrong loading this person. Please try again.
				</p>
				<Link
					to="/users/people"
					className="inline-flex items-center gap-1.5 text-sm text-zinc-600 hover:text-foreground transition-colors mt-4"
				>
					<ArrowLeft className="h-4 w-4" />
					Back to People
				</Link>
			</Page>
		);
	}

	return (
		<Page>
			<div className="mb-6">
				<Link
					to="/users/people"
					className="inline-flex items-center gap-1.5 text-sm text-zinc-600 hover:text-foreground transition-colors"
				>
					<ArrowLeft className="h-4 w-4" />
					Back to People
				</Link>
			</div>

			<div className="flex items-center gap-4 mb-6">
				<div className="w-14 h-14 rounded-full bg-foreground text-white flex items-center justify-center text-lg font-medium overflow-hidden flex-shrink-0">
					{!isAvatarError && avatarSrc ? (
						<img
							src={avatarSrc}
							alt={personDetail.name}
							className="w-full h-full object-cover"
						/>
					) : (
						<span>
							{personDetail.name
								.split(" ")
								.map((n: string) => n[0])
								.join("")
								.slice(0, 2)
								.toUpperCase()}
						</span>
					)}
				</div>
				<div>
					<h1 className="font-heading text-2xl font-semibold text-foreground">
						{personDetail.name}
					</h1>
					<p className="text-sm text-zinc-600 mt-1">{personDetail.email}</p>
				</div>
			</div>

			<div
				className="inline-flex bg-white border border-border p-1.5 rounded-xl shadow-xs mb-6"
				role="tablist"
			>
				{TABS.map(({ key, label }) => (
					<button
						key={key}
						type="button"
						role="tab"
						aria-selected={activeTab === key}
						className={[
							"px-4 py-2 text-sm font-medium rounded-lg transition-all duration-200",
							activeTab === key
								? "bg-primary text-white shadow-md shadow-primary/50"
								: "text-zinc-600 hover:text-foreground hover:bg-white/80"
						].join(" ")}
						onClick={() => handleTabChange(key)}
					>
						{label}
					</button>
				))}
			</div>

			{activeTab === "details" && (
				<PersonDetailsTab
					person={personDetail}
					stores={stores}
					isSaving={isSaving}
					onSave={(values) => {
						const payload = adaptPersonPayload(values);
						editPerson({ id, payload });
					}}
				/>
			)}

			{activeTab === "profile" &&
				(isLoadingProfile ? (
					<div className="flex items-center gap-2 py-12 justify-center">
						<div className="w-5 h-5 border-2 border-border border-t-primary rounded-full animate-spin" />
						<span className="text-sm text-zinc-600">Loading...</span>
					</div>
				) : (
					<PersonProfileTab profile={profile?.socialProfile ?? null} />
				))}

			{activeTab === "groups" &&
				(isLoadingProfile ? (
					<div className="flex items-center gap-2 py-12 justify-center">
						<div className="w-5 h-5 border-2 border-border border-t-primary rounded-full animate-spin" />
						<span className="text-sm text-zinc-600">Loading...</span>
					</div>
				) : (
					<PersonGroupsTab
						groups={profile?.socialProfile?.meeGroups ?? []}
						allGroups={allGroups}
						isAssigning={isAssigning}
						isRemoving={isRemoving}
						onAssign={(groupIds) => assignGroups({ id, groupIds })}
						onRemove={(groupIds) => removeGroups({ id, groupIds })}
					/>
				))}

			{activeTab === "admin" &&
				(isLoadingProfile ? (
					<div className="flex items-center gap-2 py-12 justify-center">
						<div className="w-5 h-5 border-2 border-border border-t-primary rounded-full animate-spin" />
						<span className="text-sm text-zinc-600">Loading...</span>
					</div>
				) : (
					<PersonAdminTab authUser={profile?.authUser ?? null} />
				))}
		</Page>
	);
};

export default PersonDetail;
